import { faArrowRight } from '@fortawesome/pro-solid-svg-icons'
import { Box, type BoxProps } from '@mantine/core'
import clsx from 'clsx'
import dayjs from 'dayjs'
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter'
import {
  type ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react'

import { useTranslation } from '@flecto/translations'
import {
  DateTimePickerInput,
  type DateTimePickerInputProps,
  Icon,
  DatePickerInput,
} from '@flecto/ui'

import { useStoreAnalytics } from '../../hooks/useStoreAnalytics'
import { useStoreBasket } from '../../hooks/useStoreBasket'
import styles from './styles.module.css'

dayjs.extend(isSameOrAfter)

export type SectionDatesSelectorProps = BoxProps & {
  size?: DateTimePickerInputProps['size']
  startAt?: string | undefined
  endAt?: string | undefined
  errorStartAt?: ReactNode
  errorEndAt?: ReactNode
  placeholderStartAt?: string
  placeholderEndAt?: string
  onChangeStartAt?: (date: string) => void
  onChangeEndAt?: (date: string) => void
  isInline?: boolean
  showCalendarIcon?: boolean
  showTimeIcon?: boolean
  withLabels?: boolean
  withDateOnly?: boolean
}

export function SectionDatesSelector({
  startAt,
  endAt,
  size,
  errorStartAt,
  errorEndAt,
  placeholderStartAt,
  placeholderEndAt,
  onChangeStartAt,
  onChangeEndAt,
  isInline = false,
  showCalendarIcon = true,
  showTimeIcon = true,
  withLabels = true,
  withDateOnly = false,
  ...props
}: SectionDatesSelectorProps) {
  const { t } = useTranslation('store', { keyPrefix: 'store-shared' })

  const [localStartAt, setLocalStartAt] = useState(startAt ?? '')
  const [localEndAt, setLocalEndAt] = useState(endAt ?? '')
  const [initialDates, setInitialDates] = useState({ startAt, endAt })
  const [shouldTrackAnalytics, setShouldTrackAnalytics] = useState(false)
  const { products } = useStoreBasket()
  const { track } = useStoreAnalytics()

  const classes = clsx(styles.wrapper, props.className, {
    [styles.inline]: isInline,
    [styles.withLabels]: withLabels,
  })

  const InputComponent = withDateOnly ? DatePickerInput : DateTimePickerInput

  // Gets next minutes interval based on current hour:minute
  const getNextMinuteIntervalFromNow = (intervalMinutes = 15) =>
    dayjs()
      .add(intervalMinutes - (dayjs().minute() % intervalMinutes), 'minute')
      .startOf('minute')
      .format('HH:mm')

  // Gets 1:15 after current hour
  const getNextHourMinus15 = () => {
    const now = dayjs()
    const next15MinIncrement = now
      .add(15 - (now.minute() % 15), 'minute')
      .startOf('minute')
    const nextHour = next15MinIncrement.add(1, 'hour').startOf('hour')
    const result = nextHour.subtract(15, 'minute')
    return result.format('HH:mm')
  }

  const minProps = useMemo(() => {
    const startAtDateOnly = dayjs(localStartAt).format('YYYY-MM-DD')
    const endAtDateOnly = dayjs(localEndAt).format('YYYY-MM-DD')

    return {
      startAt: {
        minDate: dayjs().toDate(),
        minTime:
          localStartAt && dayjs(startAtDateOnly).isToday()
            ? getNextMinuteIntervalFromNow()
            : undefined,
      },

      endAt: {
        minDate: localStartAt ? dayjs(localStartAt).toDate() : dayjs().toDate(),
        minTime:
          localStartAt &&
          localEndAt &&
          dayjs(startAtDateOnly).isSame(endAtDateOnly)
            ? dayjs(localStartAt).add(15, 'minute').format('HH:mm')
            : undefined,
      },
    }
  }, [startAt, endAt, localStartAt, localEndAt])

  const handleChangeStartAt = useCallback(
    (value: string) => {
      setShouldTrackAnalytics(true)
      let date = dayjs(value).isBefore(dayjs())
        ? dayjs().format(`YYYY-MM-DDT${getNextHourMinus15()}:00`)
        : value

      if (withDateOnly) {
        date = startAt
          ? dayjs(value).format(
              `YYYY-MM-DDT${dayjs(startAt).format('HH:mm')}:00`
            )
          : dayjs(value).format(`YYYY-MM-DDT${getNextHourMinus15()}:00`)
      }

      if (dayjs(date).isSameOrAfter(localEndAt)) {
        const end = dayjs(date).add(30, 'minute').format('YYYY-MM-DDTHH:mm:00')
        setLocalEndAt(end)
        onChangeEndAt?.(end)
      }

      setLocalStartAt(date)
      onChangeStartAt?.(date)
    },
    [withDateOnly, localEndAt, startAt]
  )

  const handleChangeEndAt = useCallback(
    (value: string) => {
      setShouldTrackAnalytics(true)
      let date = value

      if (withDateOnly) {
        date = endAt
          ? dayjs(value).format(`YYYY-MM-DDT${dayjs(endAt).format('HH:mm')}:00`)
          : dayjs(value).format(`YYYY-MM-DDT${getNextHourMinus15()}:00`)
      }

      const end = dayjs(date).isSameOrBefore(localStartAt)
        ? dayjs(localStartAt).add(30, 'minute').format('YYYY-MM-DDTHH:mm:00')
        : date

      setLocalEndAt(end)
      onChangeEndAt?.(end)
    },
    [withDateOnly, localStartAt, endAt]
  )

  useEffect(() => {
    if (startAt && startAt !== localStartAt) {
      setLocalStartAt(startAt)
    }
  }, [startAt, localStartAt])

  useEffect(() => {
    if (endAt && endAt !== localEndAt) {
      setLocalEndAt(endAt)
    }
  }, [endAt, localEndAt])

  useEffect(() => {
    if (
      shouldTrackAnalytics &&
      products.length > 0 &&
      (localStartAt !== initialDates.startAt ||
        localEndAt !== initialDates.endAt)
    ) {
      track('UPDATE__BASKET', {
        product_ids: products.map((i) => i.product.id),
        type: 'change_dates',
      })

      setInitialDates({ startAt: localStartAt, endAt: localEndAt })
    }
  }, [localStartAt, localEndAt, products, track, shouldTrackAnalytics])

  return (
    <Box {...props} className={classes}>
      <InputComponent
        popoverProps={{ withinPortal: false, zIndex: 1000 }}
        comboboxProps={{ withinPortal: false, zIndex: 1000 }}
        size={size}
        error={errorStartAt}
        value={localStartAt}
        label={withLabels && t('section_select_dates.label_start_date')}
        placeholderDate={placeholderStartAt}
        placeholder={placeholderStartAt}
        className={styles.startAt}
        minDate={minProps.startAt.minDate}
        minTime={minProps.startAt.minTime}
        showCalendarIcon={showCalendarIcon}
        showTimeIcon={showTimeIcon}
        onChange={(value) => handleChangeStartAt(value)}
      />
      <Icon icon={faArrowRight} className={styles.icon} />
      <InputComponent
        popoverProps={{ withinPortal: false }}
        comboboxProps={{ withinPortal: false }}
        size={size}
        error={errorEndAt}
        value={localEndAt}
        label={withLabels && t('section_select_dates.label_end_date')}
        placeholderDate={placeholderEndAt}
        placeholder={placeholderEndAt}
        className={styles.endAt}
        minDate={minProps.endAt.minDate}
        minTime={minProps.endAt.minTime}
        showCalendarIcon={showCalendarIcon}
        showTimeIcon={showTimeIcon}
        onChange={(value) => handleChangeEndAt(value)}
      />
    </Box>
  )
}
